import React, { useEffect, useRef, useState, useContext } from "react";
import OnePlayer from "@components/StatsConsole/Widgets/LineupWidget/parts/OnePlayer";
import { IPlayer } from "@components/StatsConsole/Widgets/LineupWidget/parts/Players";
import { LeftArrowInChipsFilters } from "@components/StatsConsole/Layout/parts/ChipsFiltersarrows/LeftArrowInChipsFilters";
import { RightArrowInChipsFilters } from "@components/StatsConsole/Layout/parts/ChipsFiltersarrows/RightArrowInChipsFilters";
import { useTheme } from "@stores/ThemeContext";
import { sortPlayers } from "@components/StatsConsole/Widgets/LineupWidget/parts/HorizontalLists/hooks/sortingPlayers";
import { getIsMobileDevice } from "@utils/functions";
import { T } from "@utils/enums";
import { useVersion } from "@utils/hooks/useVersion";
import PlayerTooltip from "@components/StatsConsole/Widgets/LineupWidget/parts/PlayerTooltip/PlayerTooltip";
import { StateContext } from "../../../../../../state/stateContext";

interface HorizontalListProps {
  mb?: string;
  ml?: string;
  players: IPlayer[];
  isInjuries?: boolean;
  name?: string | null;
  team: 'home' | 'away';
}

const HorizontalListWithArrows = ({ mb, ml, players, isInjuries = false, name = null, team }: HorizontalListProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const menuItemsHLContainerRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuArrowsVisible, setIsMenuArrowsVisible] = useState(false);
  const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState(true);
  const [isRightArrowDisabled, setIsRightArrowDisabled] = useState(true);
  const [isMenuItemsScrollable, setIsMenuItemsScrollable] = useState(false);
  const { isV1 } = useVersion();
  const ctx = useContext(StateContext);
  const { stContext, setStContext } = ctx || {};
  const { activeTooltipPlayerId } = stContext || {};
  const activeTooltipIdRef = useRef<string | null>(null);
  
  useEffect(() => {
    activeTooltipIdRef.current = activeTooltipPlayerId || null;
  }, [activeTooltipPlayerId]);
  const tooltipOpenedAtRef = useRef<number>(0);
  useEffect(() => {
    if (activeTooltipPlayerId) {
      tooltipOpenedAtRef.current = Date.now();
    }
  }, [activeTooltipPlayerId]);

  const onClickLeftArrow = () => {
    const menuItemsContainer = scrollRef.current;
    if (menuItemsContainer) {
      const currentScroll = menuItemsContainer.scrollLeft;
      const scrollAmount = Math.min(40, currentScroll);
      menuItemsContainer.scrollLeft -= scrollAmount;
    }
  }

  const onClickRightArrow = () => {
    const menuItemsContainer = scrollRef.current;
    if (menuItemsContainer) {
      const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
      const currentScroll = menuItemsContainer.scrollLeft;
      const remainingScroll = maxScroll - currentScroll;
      const scrollAmount = Math.min(40, remainingScroll);
      menuItemsContainer.scrollLeft += scrollAmount;
    }
  }

  useEffect(() => {
    const isMobileDevice = getIsMobileDevice();
    if (isMobileDevice) {
      setIsMenuArrowsVisible(false);
    } else {
      if (isMenuItemsScrollable) {
        setIsMenuArrowsVisible(true);
      } else {
        setIsMenuArrowsVisible(false);
      }
    }
  }, [isMenuItemsScrollable])

  useEffect(() => {
    const checkScrollability = () => {
      if (menuItemsHLContainerRef.current && scrollRef.current) {
        const itemsWidth = scrollRef.current.scrollWidth;
        const windowWidth = window.innerWidth;
        setIsMenuItemsScrollable(itemsWidth > windowWidth);
      } else {
        setIsMenuItemsScrollable(false);
      }
    };

    checkScrollability();
    window.addEventListener('resize', checkScrollability);

    return () => {
      window.removeEventListener('resize', checkScrollability);
    };
  }, []);

  const lastScrollLeftRef = useRef(0);
  useEffect(() => {
    const menuItemsContainer = scrollRef.current;
    if (menuItemsContainer) {
      lastScrollLeftRef.current = menuItemsContainer.scrollLeft;

      const handleScroll = () => {
        const currentScroll = menuItemsContainer.scrollLeft;
        const maxScroll = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
        setIsLeftArrowDisabled(currentScroll === 0);
        setIsRightArrowDisabled(Math.abs(currentScroll - maxScroll) < 1);

        //Hide tooltip only when there is a actual scroll (threshold)
        const delta = Math.abs(currentScroll - lastScrollLeftRef.current);
        lastScrollLeftRef.current = currentScroll;
        const justOpenedAgoMs = Date.now() - tooltipOpenedAtRef.current;
        if (delta >= 6 && activeTooltipIdRef.current && justOpenedAgoMs > 200) {
          setStContext((prev: any) => ({
            ...prev,
            activeTooltipPlayerId: null
          }));
        }
      };

      menuItemsContainer.addEventListener('scroll', handleScroll);

      //Set arrows states without closing tooltip logic
      const cur = menuItemsContainer.scrollLeft;
      const max = menuItemsContainer.scrollWidth - menuItemsContainer.clientWidth;
      setIsLeftArrowDisabled(cur === 0);
      setIsRightArrowDisabled(Math.abs(cur - max) < 1);

      return () => {
        menuItemsContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [setStContext, window.innerWidth]);

  //Calculate player center X position relative to container
  const getPlayerCenterX = (index: number): number => {
    const containerEl = menuItemsHLContainerRef.current;
    const scrollEl = scrollRef.current;
    if (!containerEl || !scrollEl) return 35;
    const items = scrollEl.querySelectorAll('[data-testid="one-player"]');
    const item = items && (items[index] as HTMLElement | undefined);
    if (!item) return 35;
    const itemRect = item.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    return itemRect.left + (itemRect.width / 2) - containerRect.left - 2;
  };


  return (
    <div className="relative overflow-visible"
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} ref={menuItemsHLContainerRef}
      data-testid={`horizontal-list-wrapper-${name}`}>
      <div data-testid="left-arrow-container" className="absolute top-[18px] left-[8px]"
        style={{
          zIndex: 110
        }}
      >
        {isMenuArrowsVisible &&
          <LeftArrowInChipsFilters
            color={theme === T.LIGHT ? (isLeftArrowDisabled ? '#111111' : '#00531D') : (isLeftArrowDisabled ? '#111111' : '#00531D')}
            onClick={onClickLeftArrow}
            isArrowHidden={isLeftArrowDisabled}
            isVisibleOnHover={isHovered}
            isHorizontalList={true}
            width="32px"
            height="32px"
          />
        }
      </div>
      <div data-testid={`horizontal-list-container-${name}`}
        ref={scrollRef}
        className={`min-h-[68px] max-w-full flex flex-row justify-start items-center  ${mb} pl-[4px] overflow-x-scroll overflow-y-visible
        gap-[4px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]`}
        style={{ backgroundColor: isV1 ? (theme === T.LIGHT ? '#FFFFFF' : '#333333') : (theme === T.LIGHT ? '#FFFFFF' : '#222222') }}
      >
        {sortPlayers(players, isInjuries)
          .map((player: IPlayer, index: number) => (
            <OnePlayer
              key={index}
              team={team}
              width="82px"
              player={player}
              id={index.toString() + player?.name}
              isHorizontalList={true}
              isInjuries={isInjuries}
              isLineup={false}
            />
          ))}
      </div>
      <div data-testid="right-arrow-container" className="absolute top-[18px] right-[8px] z-100"
        style={{
          zIndex: 110
        }}>
        {isMenuArrowsVisible &&
          <RightArrowInChipsFilters
            color={theme === T.LIGHT ? (isRightArrowDisabled ? '#111111' : '#00531D') : (isRightArrowDisabled ? '#111111' : '#00531D')}
            isArrowHidden={isRightArrowDisabled}
            onClick={onClickRightArrow}
            isVisibleOnHover={isHovered}
            isHorizontalList={true}
            width="32px"
            height="32px"
          />
        }
      </div>

      {activeTooltipPlayerId && sortPlayers(players, isInjuries).map((player: IPlayer, index: number) => {
        const playerId = index.toString() + player?.name;
        if (activeTooltipPlayerId === playerId) {
          const centerX = getPlayerCenterX(index);
          return (
            <PlayerTooltip
              key={playerId}
              player={player}
              parentWidth="82px"
              containerRef={menuItemsHLContainerRef}
              playerCenterX={centerX}
              setShowTooltip={() => { }}
              isHorizontalList={true}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export default HorizontalListWithArrows;
